<template>
  <form
    class="reset-password"
    novalidate
    @submit.prevent="send"
  >
    <div class="reset-password__field">
      <label for="userEmail"> E-mail </label>
      <input
        id="userEmail"
        v-model="email"
        name="userEmail"
        type="email"
        disabled
      />
    </div>

    <div
      class="reset-password__field"
      :class="{
        'reset-password__field--error':
          !isValidPassword && isDirtyField('userPassword'),
      }"
    >
      <label for="userPassword"> Nova senha </label>
      <input
        id="userPassword"
        v-model="password"
        name="userPassword"
        :type="passwordVisible ? 'text' : 'password'"
        @blur="setDirty('userPassword')"
        @focus="removeError('userPassword')"
      />
      <button
        class="show-password"
        type="button"
        @click.prevent="() => (passwordVisible = !passwordVisible)"
      >
        <IconEye v-if="!passwordVisible" />
        <IconEyeClosed v-else />
      </button>
    </div>

    <div
      class="reset-password__field"
      :class="{
        'reset-password__field--error':
          isDirtyField('userPasswordConfirmation') &&
          (!isValidPasswordConfirmation || !isSamePassword),
      }"
    >
      <label for="userPasswordConfirmation"> Confirmação de nova senha </label>
      <input
        id="userPasswordConfirmation"
        v-model="passwordConfirmation"
        name="userPasswordConfirmation"
        :type="passwordConfirmationVisible ? 'text' : 'password'"
        @blur="setDirty('userPasswordConfirmation')"
        @focus="removeError('userPasswordConfirmation')"
      />
      <button
        class="show-password"
        type="button"
        @click.prevent="
          () => (passwordConfirmationVisible = !passwordConfirmationVisible)
        "
      >
        <IconEye v-if="!passwordConfirmationVisible" />
        <IconEyeClosed v-else />
      </button>
    </div>

    <ul class="reset-password__requirements">
      <li
        v-for="requirement in requirementsList"
        :key="requirement.id"
        :class="requirementsValidationClass(requirement, 'userPassword')"
      >
        <IconSuccess
          v-if="requirement.validation()"
          size="xs"
        />
        {{ requirement.text }}
      </li>
    </ul>

    <div class="reset-password__field">
      <button
        class="reset-password__cta reset-password__cta--no-margin"
        :class="{
          'reset-password__cta--disabled': !isValidFields || loading,
          'request-password__cta--loading': loading,
        }"
        :disabled="!isValidFields || loading"
        type="submit"
      >
        <span>
          {{ loading ? 'Salvando' : 'Salvar' }}
        </span>
      </button>

      <transition name="fade">
        <p
          v-if="submitError"
          class="reset-password__field--warning"
        >
          {{ submitError }}
        </p>
      </transition>
    </div>
  </form>
</template>

<script>
import decodeJWT from '@/helpers/decodeJWT';
import IconEye from '@/components/icons/IconEye.vue';
import IconEyeClosed from '@/components/icons/IconEyeClosed.vue';
import IconSuccess from '@/components/icons/IconSuccess.vue';
import passwordValidator from '@/validation/validators/password';
import errosLogin from '@/helpers/errorsLogin';

export default {
  name: 'ResetPassword',
  components: {
    IconEye,
    IconEyeClosed,
    IconSuccess,
  },
  data() {
    return {
      email: '',
      password: '',
      passwordConfirmation: '',
      passwordVisible: false,
      passwordConfirmationVisible: false,
      token: '',
      submitError: '',
      loading: false,
      dirtyFields: [],
      requirementsList: [
        {
          id: 'length',
          validation: this.hasMinLength,
          text: 'A senha precisa ter ao menos 8 caracteres',
        },
        {
          id: 'uppercase',
          validation: this.hasUppercase,
          text: 'A senha precisa ter ao menos 1 letra maiúscula',
        },
        {
          id: 'lowercase',
          validation: this.hasLowercase,
          text: 'A senha precisa ter ao menos 1 letra minúscula',
        },
        {
          id: 'number',
          validation: this.hasNumber,
          text: 'A senha precisa ter ao menos 1 número',
        },
      ],
    };
  },
  computed: {
    isValidPassword() {
      return passwordValidator(this.password);
    },
    isValidPasswordConfirmation() {
      return passwordValidator(this.passwordConfirmation);
    },
    isValidFields() {
      return (
        this.isValidPassword &&
        this.isValidPasswordConfirmation &&
        this.isSamePassword
      );
    },
    isSamePassword() {
      return this.password === this.passwordConfirmation;
    },
  },
  mounted() {
    const { flow } = this.$route.query;
    const token = flow ? this.$route.query.token : this.$store.state.resetToken;
    const decoded = decodeJWT(token);

    this.token = token;
    this.email = decoded.email;
  },
  methods: {
    setDirty(field) {
      if (!this.dirtyFields.includes(field)) {
        this.dirtyFields.push(field);
      }
    },
    isDirtyField(field) {
      return this.dirtyFields.includes(field);
    },
    removeError(field) {
      this.submitError = '';

      this.dirtyFields = this.dirtyFields.filter((item) => {
        return field !== item;
      });
    },
    hasUppercase() {
      return /[A-Z]+/.test(this.password);
    },
    hasLowercase() {
      return /[a-z]+/.test(this.password);
    },
    hasNumber() {
      return /[0-9]+/.test(this.password);
    },
    hasMinLength() {
      return this.password.length >= 8;
    },
    requirementsValidationClass(req, field) {
      const validationClass = [];

      if (req.validation()) {
        validationClass.push('reset-password__requirements--success');
      } else {
        validationClass.push('reset-password__requirements--custom-icon');
      }

      if (!req.validation() && this.isDirtyField(field)) {
        validationClass.push('reset-password__requirements--error');
      }

      return validationClass;
    },
    send() {
      this.loading = true;

      const userBody = {
        token: this.token,
        password: this.password,
      };

      this.$store
        .dispatch('resetPassword', userBody)
        .then((token) => {
          this.$store.commit('SET_RESET_TOKEN', '');

          const isGlobalUser = !token;

          if (isGlobalUser) {
            this.$router.push({ name: 'login' });
            this.$emit('handleFlow', 'signin');
          } else {
            // force refresh
            window.location.href = `${window.location.origin}/`;
          }
        })
        .catch((err) => {
          this.loading = false;
          this.submitError = errosLogin(err.response?.data?.message);
        });
    },
  },
};
</script>

<style lang="scss" scoped>
.login .reset-password__requirements {
  font-size: $size-smaller;
  font-weight: $lb-medium;
  padding: 8px 16px 0 16px;

  li {
    display: flex;
    align-items: center;
    position: relative;

    .icon {
      margin-right: 4px;
    }
  }

  &--custom-icon {
    padding-left: 20px;
    color: $gray-70;

    &::before {
      content: '';
      width: 4px;
      height: 4px;
      position: absolute;
      left: 5px;
      background: $gray-70;
      border-radius: 50%;
    }
  }

  &--error {
    color: $danger;

    &::before {
      background: $danger;
    }
  }

  &--success {
    color: $success;
  }

  &--center {
    text-align: center;
  }
}
</style>
